import { FC, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { EvFlexRow, EvSection, EvSwitch } from '@evinced-private/ui-common';

import {
	MFA_SESSION_SCANS_INCLUDE_ZERO_ISSUE_TOGGLE_DAID,
	MFA_SESSION_SCANS_TAB_DAID,
	MFA_SESSION_SCANS_TABLE_DAID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import { MfaSingleSessionFiltersSection } from 'src/apps/mfa/pages/single-session/filters-section/MfaSingleSessionFiltersSection';
import {
	MobileErrorMessage,
	MobileErrorType
} from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { useMobileScansListQuery } from 'src/apps/mobile-common/hooks/useMobileScansListQuery';
import { IMobileScansListRow } from 'src/apps/mobile-common/services/mobile-scans-list';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import {
	MFA_SESSION_ALL_ISSUES_TABLE_ID,
	MFA_SESSION_SCANS_TABLE_ID
} from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { MFA_SESSION_SCANS_LABELS } from './MfaSessionScans.labels';
import { IMfaSessionScansProps } from './MfaSessionScans.types';
import { getMfaSessionScansColumns } from './MfaSessionScansTableHelper';

import './MfaSessionScans.scss';

const EMPTY_SUMMARY = {
	scansCount: 0,
	issuesCount: 0,
	elementsWithIssuesCount: 0
};

export const MfaSessionScans: FC<IMfaSessionScansProps> = ({ sessionId }) => {
	const history = useHistory();
	const { getTableStateById, onFilterChange } = useTableState();
	const [includeScansWithoutIssues, setIncludeScansWithoutIssues] = useState(false);

	const tableId = useMemo(
		() => buildFullTableId(MFA_SESSION_SCANS_TABLE_ID, sessionId),
		[sessionId]
	);
	const issuesTableId = useMemo(
		() => buildFullTableId(MFA_SESSION_ALL_ISSUES_TABLE_ID, sessionId),
		[sessionId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const scansQuery = useMobileScansListQuery({
		runId: sessionId,
		productName: ProductType.MOBILE_FLOW_ANALYZER,
		includeScansWithoutIssues,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filterOptions: filters
	});

	const handleToggleChange = useCallback((value: boolean): void => {
		setIncludeScansWithoutIssues(value);
	}, []);

	const handleScanNameClick = useCallback(
		(scan: IMobileScansListRow): void => {
			onFilterChange(issuesTableId, {
				scans: [{ value: scan.scanName, label: scan.scanName }]
			});
		},
		[issuesTableId, onFilterChange]
	);

	const columns = useMemo(
		() => getMfaSessionScansColumns(handleScanNameClick, history.location?.pathname),
		[handleScanNameClick, history.location?.pathname]
	);

	if (scansQuery.isError) {
		return (
			<MobileErrorMessage
				errorType={MobileErrorType.LOADING_ERROR}
				productType={ProductType.MOBILE_FLOW_ANALYZER}
			/>
		);
	}

	const { rows = [], total = 0, summary = EMPTY_SUMMARY } = scansQuery.data ?? {};
	const isTableLoading = scansQuery.isPreviousData;

	return (
		<EvSection
			className="mfa-session-scans"
			dataAutomationId={MFA_SESSION_SCANS_TAB_DAID}
			ariaLabel={MFA_SESSION_SCANS_LABELS.SECTION_ARIA_LABEL}
		>
			<MfaSingleSessionFiltersSection
				sessionId={sessionId}
				filters={filters}
				ariaLabel={MFA_SESSION_SCANS_LABELS.FILTERS_ARIA_LABEL}
				onFilterChange={onFilterChange}
				tableId={tableId}
			/>
			<EvFlexRow justify="END">
				<EvSwitch
					dataAutomationId={MFA_SESSION_SCANS_INCLUDE_ZERO_ISSUE_TOGGLE_DAID}
					checked={includeScansWithoutIssues}
					onChange={handleToggleChange}
					label={MFA_SESSION_SCANS_LABELS.INCLUDE_ZERO_ISSUE_TOGGLE_LABEL}
				/>
			</EvFlexRow>
			<EvSection ariaLabel={MFA_SESSION_SCANS_LABELS.TABLE_ARIA_LABEL} skipLinkId="main-section">
				<PlatformTable
					dataAutomationId={MFA_SESSION_SCANS_TABLE_DAID}
					isTableLoading={isTableLoading}
					tableId={tableId}
					tableData={rows}
					totalCount={total}
					totalPaginationableResults={total}
					className="mfa-session-scans__table"
					persistTableState
					columns={columns}
					options={{
						keyField: 'scanId',
						title: MFA_SESSION_SCANS_LABELS.TABLE_TITLE_WITH_SUMMARY(summary),
						caption: MFA_SESSION_SCANS_LABELS.TABLE_CAPTION,
						pagination: true,
						dataType: MFA_SESSION_SCANS_LABELS.SCANS_DATA_TYPE_WITH_SUMMARY(summary),
						remote: true
					}}
					withUrlSwitcher={false}
				/>
			</EvSection>
		</EvSection>
	);
};
